/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { getPercentage } from '../../utils';
import PieChartIcon from '@mui/icons-material/PieChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import SpeedIcon from '@mui/icons-material/Speed';

function getThresholdCounts(data, total, percentages) {
  const sortedEfforts = Object.values(data).sort((a, b) => b - a);

  return percentages.map(percent => {
    const target = total * (percent / 100);
    let sum = 0;
    let count = 0;

    for (const effort of sortedEfforts) {
      sum += effort;
      count++;
      if (sum >= target) break;
    }

    return { percent, count };
  });
}

const MetricItem = ({ cardData, top10Authors, displayMode, isDarkMode }) => {
  const isInvestment = displayMode === 'investment';
  const unitPrefix = isInvestment ? '$' : '';
  const effortTitle = isInvestment
    ? 'Estimated Cost Without AI ($)'
    : 'Baseline Effort (REV)';

  const results = getThresholdCounts(
    top10Authors,
    cardData?.totalEffort,
    [50, 80]
  );

  return (
    <div className="col-span-12">
      {/* Effort Summary */}
      <div className="mb-4">
        <div
          className={`overflow-hidden rounded-[28px] border shadow-[0_18px_50px_rgba(15,23,42,0.08)] ${
            isDarkMode
              ? 'border-strokedark bg-boxdark'
              : 'border-slate-200 bg-white'
          }`}
        >
          <div
            className={`relative overflow-hidden border-b px-5 py-6 sm:px-7 ${
              isDarkMode
                ? 'border-strokedark bg-boxdark'
                : 'border-slate-200 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.16),transparent_34%),radial-gradient(circle_at_top_right,rgba(168,85,247,0.16),transparent_30%),linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)]'
            }`}
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#8b5cf6_0%,#3b82f6_52%,#22c55e_100%)]" />
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h5
                    className={`text-base font-bold tracking-tight sm:text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}
                  >
                    {effortTitle}
                  </h5>
                </div>
                <p
                  className={`mt-2 text-sm leading-6 lg:whitespace-nowrap ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}
                >
                  {isInvestment
                    ? 'Estimated Cost Without AI shows the financial equivalent of the baseline effort required to deliver the same work without AI assistance.'
                    : 'Baseline REV estimates the effort a typical developer would need to deliver the same change without AI assistance.'}
                </p>
              </div>

              <div className="flex items-center justify-between gap-4 lg:min-w-[280px] lg:justify-end">
                <div className="min-w-0 text-left lg:text-right">
                  <span
                    className={`block text-3xl font-black tracking-tight sm:text-4xl ${isDarkMode ? 'text-white' : 'text-slate-950'}`}
                  >
                    {unitPrefix}
                    {Number(
                      Math.round(cardData?.totalEffort || 0)
                    ).toLocaleString('en-US')}
                  </span>
                </div>
                <div
                  className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border shadow-[0_10px_30px_rgba(59,130,246,0.15)] ${isDarkMode ? 'border-strokedark bg-meta-4 text-slate-200' : 'border-slate-200 bg-white text-slate-700'}`}
                >
                  <SpeedIcon fontSize="large" />
                </div>
              </div>
            </div>
          </div>

          {/* AI Productivity Multiplier */}
          {cardData?.totalEffortDev > 0 && (
            <div
              className={`border-t px-5 py-4 sm:px-7 ${
                isDarkMode ? 'border-strokedark' : 'border-slate-200'
              }`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <h6
                    className={`text-xs font-semibold uppercase tracking-wider ${
                      isDarkMode ? 'text-slate-400' : 'text-slate-500'
                    }`}
                  >
                    AI Leverage
                  </h6>
                  <p
                    className={`mt-1 text-sm ${
                      isDarkMode ? 'text-slate-300' : 'text-slate-600'
                    }`}
                  >
                    Your development productivity was leveraged by AI
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-baseline gap-1">
                    <span
                      className={`text-3xl font-black tracking-tight ${
                        isDarkMode ? 'text-purple-300' : 'text-purple-600'
                      }`}
                    >
                      {(
                        (cardData?.totalEffort || 0) /
                        (cardData?.totalEffortDev || 1)
                      ).toFixed(1)}
                    </span>
                    <span
                      className={`text-2xl font-bold ${
                        isDarkMode ? 'text-purple-400/70' : 'text-purple-500/70'
                      }`}
                    >
                      x
                    </span>
                  </div>
                  <div
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border shadow-md ${
                      isDarkMode
                        ? 'border-strokedark bg-meta-4 text-purple-300'
                        : 'border-purple-200 bg-white text-purple-600'
                    }`}
                  >
                    <TrendingUpIcon />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div
          className={`rounded-2xl p-6 shadow-md ${isDarkMode ? 'bg-boxdark' : 'bg-white'}`}
        >
          {/* Header */}
          <div className="flex flex-col gap-4 border-b border-gray-200 pb-4 sm:flex-row sm:items-center sm:justify-between dark:border-gray-700">
            <div>
              <h5 className="text-xs font-semibold tracking-wider text-gray-500 uppercase">
                Total Commits
              </h5>
              <span className="text-3xl font-extrabold text-gray-900 dark:text-white">
                {Number(Math.round(cardData?.totalCommit || 0)).toLocaleString(
                  'en-US'
                )}
              </span>
            </div>

            <div className="flex items-center justify-center w-14 h-14 bg-green-500 text-white rounded-full shadow-lg">
              <TrendingUpIcon fontSize="large" />
            </div>
          </div>

          {/* Top Performer */}
          <div className="mt-4 flex flex-col gap-3 rounded-lg bg-green-50 p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between dark:bg-green-900/20">
            <div className="min-w-0">
              <p className="text-xs font-bold text-green-700 dark:text-green-400 tracking-wide uppercase">
                🏆 Top Performer
              </p>
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                {cardData?.topPerformerAuthor?.author_name}
                <span className="text-gray-500">
                  {' '}
                  —{' '}
                  {Number(
                    Math.round(cardData?.topPerformerAuthor?.effort || 0)
                  ).toLocaleString('en-US')}
                  {isInvestment ? ' $' : ''}
                </span>
              </p>
            </div>
            <span className="flex w-fit items-center rounded-md bg-green-100 px-2 py-1 text-sm font-bold text-green-700 dark:bg-green-800 dark:text-green-300">
              {getPercentage(
                cardData?.topPerformerAuthor?.effort,
                cardData?.totalEffort
              )}
              % of baseline effort
            </span>
          </div>

          {/* Lowest Performer */}
          <div className="mt-3 flex flex-col gap-3 rounded-lg bg-red-50 p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between dark:bg-red-900/20">
            <div className="min-w-0">
              <p className="text-xs font-bold text-red-700 dark:text-red-400 tracking-wide uppercase">
                ⚠️ Lowest Performer
              </p>
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                {cardData?.lowestPerformerAuthor?.author_name}
                <span className="text-gray-500">
                  {' '}
                  — {Math.round(cardData?.lowestPerformerAuthor?.effort || 0)}
                  {isInvestment ? ' $' : ''}
                </span>
              </p>
            </div>
            <span className="flex w-fit items-center rounded-md bg-red-100 px-2 py-1 text-sm font-bold text-red-700 dark:bg-red-800 dark:text-red-300">
              {getPercentage(
                cardData?.lowestPerformerAuthor?.effort,
                cardData?.totalEffort
              )}
              % of baseline effort
            </span>
          </div>
        </div>

        <div
          className={`rounded-2xl p-6 shadow-md ${isDarkMode ? 'bg-boxdark' : 'bg-white'}`}
        >
          {/* Header */}
          <div className="flex flex-col gap-4 border-b border-gray-200 pb-4 sm:flex-row sm:items-center sm:justify-between dark:border-gray-700">
            <div>
              <h5 className="text-xs font-semibold tracking-wider text-gray-500 uppercase">
                Total Developers
              </h5>
              <span className="text-3xl font-extrabold text-gray-900 dark:text-white">
                {Object.keys(top10Authors).length}
              </span>
            </div>
            <div className="flex items-center justify-center w-14 h-14 bg-blue-500 text-white rounded-full shadow-lg">
              <GroupIcon fontSize="large" />
            </div>
          </div>

          {/* %50 Authors */}
          <div className="mt-4 flex flex-col gap-3 rounded-lg bg-indigo-50 p-3 py-6 sm:flex-row sm:items-center sm:justify-between dark:bg-indigo-900/20">
            <div className="flex items-start">
              <PieChartIcon
                fontSize="small"
                className="text-indigo-600 dark:text-indigo-400 mr-2"
              />
              <p className="text-sm font-bold text-gray-800 dark:text-gray-100">
                <span className="text-indigo-600 dark:text-indigo-400">
                  50%
                </span>{' '}
                of total development effort
              </p>
            </div>
            <span className="text-sm font-bold text-indigo-700 dark:text-indigo-300">
              Driven by Top {results[0].count}{' '}
              {results[0].count === 1 ? 'developer' : 'developers'}
            </span>
          </div>

          {/* %80 Authors */}
          <div className="mt-3 flex flex-col gap-3 rounded-lg bg-purple-50 p-3 py-6 sm:flex-row sm:items-center sm:justify-between dark:bg-purple-900/20">
            <div className="flex items-start">
              <PieChartIcon
                fontSize="small"
                className="text-purple-600 dark:text-purple-400 mr-2"
              />

              <p className="text-sm font-bold text-gray-800 dark:text-gray-100">
                <span className="text-purple-600 dark:text-purple-400">
                  80%
                </span>{' '}
                of total development effort
              </p>
            </div>
            <span className="text-sm font-bold text-purple-700 dark:text-purple-300">
              Driven by Top {results[1].count}{' '}
              {results[1].count === 1 ? 'developer' : 'developers'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const MetricsGrid = ({
  cardData,
  loading,
  top10Authors,
  displayMode,
  isDarkMode
}) => {
  if (loading) {
    return (
      <div className="col-span-12 pt-5">
        <div
          className={`mb-4 overflow-hidden rounded-[28px] border shadow-[0_18px_50px_rgba(15,23,42,0.08)] ${
            isDarkMode
              ? 'border-strokedark bg-boxdark'
              : 'border-slate-200 bg-white'
          }`}
        >
          <div
            className={`relative overflow-hidden border-b px-5 py-6 sm:px-7 ${
              isDarkMode
                ? 'border-strokedark bg-boxdark'
                : 'border-slate-200 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.1),transparent_34%),radial-gradient(circle_at_top_right,rgba(168,85,247,0.1),transparent_30%),linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)]'
            }`}
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#8b5cf6_0%,#3b82f6_52%,#22c55e_100%)] opacity-70" />
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex-1">
                <div
                  className={`h-7 w-56 animate-pulse rounded-xl ${
                    isDarkMode ? 'bg-slate-700' : 'bg-slate-200'
                  }`}
                />
                <div
                  className={`mt-3 h-5 w-full max-w-3xl animate-pulse rounded-xl ${
                    isDarkMode ? 'bg-slate-800' : 'bg-slate-100'
                  }`}
                />
              </div>
              <div className="flex items-center gap-4 lg:min-w-[280px] lg:justify-end">
                <div
                  className={`h-12 w-40 animate-pulse rounded-2xl ${
                    isDarkMode ? 'bg-slate-700' : 'bg-slate-200'
                  }`}
                />
                <div
                  className={`h-14 w-14 animate-pulse rounded-2xl ${
                    isDarkMode ? 'bg-meta-4' : 'bg-slate-100'
                  }`}
                />
              </div>
            </div>
          </div>

          <div className="px-5 py-5 sm:px-7 sm:py-6">
            <div className="flex items-center justify-between gap-3">
              <div
                className={`h-4 w-40 animate-pulse rounded-lg ${
                  isDarkMode ? 'bg-slate-700' : 'bg-slate-200'
                }`}
              />
              <div
                className={`h-4 w-44 animate-pulse rounded-lg ${
                  isDarkMode ? 'bg-slate-800' : 'bg-slate-100'
                }`}
              />
            </div>
            <div
              className={`mt-4 h-4 w-full overflow-hidden rounded-full ${
                isDarkMode ? 'bg-meta-4' : 'bg-slate-100'
              }`}
            >
              <div className="h-full w-2/3 animate-pulse rounded-full bg-[linear-gradient(90deg,#c4b5fd_0%,#93c5fd_100%)]" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <div
            className={`rounded-2xl border p-6 shadow-md ${
              isDarkMode
                ? 'border-strokedark bg-boxdark'
                : 'border-slate-200 bg-white'
            }`}
          >
            <div
              className={`flex items-center justify-between border-b pb-4 ${
                isDarkMode ? 'border-strokedark' : 'border-slate-200'
              }`}
            >
              <div>
                <div
                  className={`h-4 w-28 animate-pulse rounded-lg ${
                    isDarkMode ? 'bg-slate-700' : 'bg-slate-200'
                  }`}
                />
                <div
                  className={`mt-3 h-9 w-24 animate-pulse rounded-xl ${
                    isDarkMode ? 'bg-slate-800' : 'bg-slate-100'
                  }`}
                />
              </div>
              <div
                className={`h-14 w-14 animate-pulse rounded-full ${
                  isDarkMode ? 'bg-meta-4' : 'bg-slate-100'
                }`}
              />
            </div>
            <div
              className={`mt-4 h-24 animate-pulse rounded-2xl ${
                isDarkMode ? 'bg-slate-800' : 'bg-slate-100'
              }`}
            />
            <div
              className={`mt-3 h-24 animate-pulse rounded-2xl ${
                isDarkMode ? 'bg-slate-800' : 'bg-slate-100'
              }`}
            />
          </div>

          <div
            className={`rounded-2xl border p-6 shadow-md ${
              isDarkMode
                ? 'border-strokedark bg-boxdark'
                : 'border-slate-200 bg-white'
            }`}
          >
            <div
              className={`flex items-center justify-between border-b pb-4 ${
                isDarkMode ? 'border-strokedark' : 'border-slate-200'
              }`}
            >
              <div>
                <div
                  className={`h-4 w-32 animate-pulse rounded-lg ${
                    isDarkMode ? 'bg-slate-700' : 'bg-slate-200'
                  }`}
                />
                <div
                  className={`mt-3 h-9 w-16 animate-pulse rounded-xl ${
                    isDarkMode ? 'bg-slate-800' : 'bg-slate-100'
                  }`}
                />
              </div>
              <div
                className={`h-14 w-14 animate-pulse rounded-full ${
                  isDarkMode ? 'bg-meta-4' : 'bg-slate-100'
                }`}
              />
            </div>
            <div
              className={`mt-4 h-20 animate-pulse rounded-2xl ${
                isDarkMode ? 'bg-slate-800' : 'bg-slate-100'
              }`}
            />
            <div
              className={`mt-3 h-20 animate-pulse rounded-2xl ${
                isDarkMode ? 'bg-slate-800' : 'bg-slate-100'
              }`}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 xl:grid-cols-4 pt-5">
        <MetricItem
          cardData={cardData}
          top10Authors={top10Authors}
          displayMode={displayMode}
          isDarkMode={isDarkMode}
        />
      </div>
    </>
  );
};

MetricItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  type: PropTypes.string,
  changeType: PropTypes.oneOf(['success', 'error']).isRequired,
  description: PropTypes.string.isRequired,
  cardData: PropTypes.object,
  top10Authors: PropTypes.object,
  isDarkMode: PropTypes.bool
};

export default MetricsGrid;
